import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react'
import {sessionStorageStore} from './session-storage-store'
import type {Listener} from './create-store'
import type {ClearKeysOptions, WriteOptions} from './types'
import {validOrDefault, type Validator} from './validation'

const {
  cache,
  readStorage,
  writeStorage,
  resetStorage,
  subscribe,
  clearKeys,
  clearKeysByPrefix,
  getItemsByPrefix,
  clearAll: clearAllSessionStorage,
} = sessionStorageStore

/**
 * Clears all store-tracked sessionStorage keys, their in-memory cache,
 * and the underlying browser sessionStorage.
 */
export function clearAllSessionStorageCache(): void {
  clearAllSessionStorage()
  if (typeof window !== 'undefined') {
    window.sessionStorage.clear()
  }
}

/**
 * Clears specified keys from sessionStorage.
 * Pass `{silent: true}` to skip notifying subscribers and only remove from browser storage.
 */
export function clearSessionStorage(keys: string[], options?: ClearKeysOptions): void {
  clearKeys(keys, options)
}

/**
 * Get all items whose keys start with a prefix
 */
export function getSessionStorageItemsForKeyPrefix<T = unknown>(prefix: string): T[] {
  return getItemsByPrefix<T>(prefix)
}

/**
 * Clear all keys matching a prefix from sessionStorage
 */
export function clearSessionStorageByKeyPrefix(prefix: string): void {
  clearKeysByPrefix(prefix)
}

interface SessionStorageOptions<T> {
  /**
   * Type validation function or TypeBox-style validator object to validate read values. If an
   * encountered value fails validation, it will be silently discarded. For complex types, a TypeBox
   * validator can be used:
   * ```ts
   * const schema = Type.Object(...)
   * const validator = Compile(schema)
   * //...
   * useSessionStorage(key, initialValue, {validator})
   * ```
   */
  validator: Validator<T>
}

type UseSessionStorageResult<T> = [
  value: T,
  setValue: (value: T | ((prev: T) => T), options?: WriteOptions) => void,
  reset: () => void,
]

/** @deprecated Omitting `options.validator` is unsafe. Provide a `validator` in an `options` object as the third argument. */
export function useSessionStorage<T>(key: string, initialValue: T): UseSessionStorageResult<T>
/**
 * React hook for sessionStorage with cross-component synchronization.
 *
 * @param key Unique key for this entry.
 * **Caution**: storage is shared across the entire app. If the same key is used in multiple places, take care to
 * ensure both callsites use matching data types. If the types conflict, they will clash with each other and result in
 * discarded data due to failed validation checks.
 * @param initialValue Initial / default value.
 * @param options Additional options including validator function.
 * @returns `[value, setValue, reset]` tuple.
 */
export function useSessionStorage<T>(
  key: string,
  initialValue: T,
  options: SessionStorageOptions<T>,
): UseSessionStorageResult<T>
export function useSessionStorage<T>(
  key: string,
  initialValue: T,
  options?: SessionStorageOptions<T>,
): UseSessionStorageResult<T> {
  // Use ref for initialValue so it's available in callbacks without stale closure issues
  const initialValueRef = useRef(initialValue)
  useEffect(() => {
    initialValueRef.current = initialValue
  })

  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  })

  // Seed lazily inside getSnapshot (idempotent) instead of during render
  // to avoid mutating external state in the render phase (concurrent-safe).
  // Uses initialValue directly (not ref) because getSnapshot runs during render
  // before the effect that updates initialValueRef.
  const getSnapshot = useCallback((): T => {
    if (!cache.has(key)) {
      readStorage(key, initialValue)
    }
    return validOrDefault(cache.get(key), initialValue, options?.validator)
  }, [key, initialValue, options?.validator])

  const getServerSnapshot = useCallback((): T => {
    return initialValue
  }, [initialValue])

  const subscribeToStore = useCallback((listener: Listener) => subscribe(key, listener), [key])

  const value = useSyncExternalStore(subscribeToStore, getSnapshot, getServerSnapshot)

  const setValue = useCallback(
    (next: T | ((prev: T) => T), writeOptions?: WriteOptions) => {
      const current = cache.has(key)
        ? validOrDefault(cache.get(key), initialValueRef.current, optionsRef.current?.validator)
        : initialValueRef.current

      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(current) : next
      // Treat undefined as a reset to avoid writing "undefined" string to storage
      if (resolved === undefined) {
        resetStorage(key, initialValueRef.current)
      } else {
        writeStorage(key, resolved, writeOptions)
      }
    },
    [key],
  )

  const reset = useCallback(() => {
    resetStorage(key, initialValueRef.current)
  }, [key])

  return [value, setValue, reset] as const
}
